import { logger } from './ProfessionalLogger';
/**
 * PerformanceMonitor.ts;
 * Moniteur de performances en temps r\u00e9el;
 */
interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  unit: string;
}
interface PerformanceThresholds {
  fps: number;
  memory: number;
  renderTime: number;
  operationTime: number;
}
export class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private thresholds: PerformanceThresholds;
  private observers: ((metrics: PerformanceMetric[]) => void)[] = [];
  private isMonitoring = false;
  private frameCount = 0;
  private lastFrameTime = 0;
  private fpsHistory: number[] = [];
  private maxHistorySize = 30; // 30 secondes \u00e0 60fps (optimis\u00e9,);
  private lastWarningMessages: Map<string, number> = new Map(); // Cache des derniers warnings
  private warningThrottleMs = 5000; // Throttle de 5 secondes pour les warnings
  constructor(thresholds: Partial<PerformanceThresholds> = {}) {
    this.thresholds = {
      fps: 30, // FPS minimum acceptable;
      memory: 100 * 1024 * 1024, // 100MB;
      renderTime: 16, // 16ms (60fps);
      operationTime: 100, // 100ms;
      ...thresholds,
    };
  }
  /**
   * D\u00e9marre le monitoring des performances;
   */
  public startMonitoring(): void {
    if (this.isMonitoring) return;
    this.isMonitoring = true;
    this.startFPSMonitoring();
    this.startMemoryMonitoring();
    logger.info('\ud83d\udcca Performance monitoring started');
  }
  /**
   * Arr\u00eate le monitoring des performances;
   */
  public stopMonitoring(): void {
    this.isMonitoring = false;
    logger.info('\ud83d\udcca Performance monitoring stopped');
  }
  /**
   * Enregistre une m\u00e9trique de performance;
   */
  public recordMetric(name: string, value: number, unit: string = 'ms'): void {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      unit,
    };
    this.metrics.push(metric);
    // Garder seulement les 1000 derni\u00e8res m\u00e9triques;
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }
    // Nettoyer les m\u00e9triques anciennes;
    this.cleanupOldMetrics();
    // V\u00e9rifier les seuils;
    this.checkThresholds(metric);
    // Notifier les observateurs;
    this.notifyObservers();
  }
  /**
   * Mesure le temps d'ex\u00e9cution d'une fonction;
   */
  public measureFunction<T>(name: string, fn: () => T, unit: string = 'ms'): T {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    this.recordMetric(name, end - start, unit);
    return result;
  }
  /**
   * Nettoie automatiquement les m\u00e9triques anciennes;
   */
  private cleanupOldMetrics(): void {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes;
    this.metrics = this.metrics.filter(metric => now - metric.timestamp < maxAge);
    // Limiter le nombre total de m\u00e9triques;
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-500); // Garder les 500 plus r\u00e9centes;
    }
  }
  /**
   * Mesure le temps d'ex\u00e9cution d'une fonction asynchrone;
   */
  public async measureAsyncFunction<T>(
    name: string,
    fn: () => Promise<T>,
    unit: string = 'ms'
  ): Promise<T> {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    this.recordMetric(name, end - start, unit);
    return result;
  }
  /**
   * Obtient les m\u00e9triques actuelles;
   */
  public getCurrentMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }
  /**
   * Obtient les m\u00e9triques par nom;
   */
  public getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter(m => m.name === name);
  }
  /**
   * Obtient la moyenne des m\u00e9triques par nom;
   */
  public getAverageMetric(name: string): number {
    const metrics = this.getMetricsByName(name);
    if (metrics.length === 0) return 0;
    const sum = metrics.reduce((acc, m) => acc + m.value, 0);
    return sum / metrics.length;
  }
  /**
   * Obtient les statistiques de performance;
   */
  public getPerformanceStats(): {
    fps: number;
    memory: number;
    averageRenderTime: number;
    averageOperationTime: number;
    totalMetrics: number;
  } {
    const currentFPS = this.getCurrentFPS();
    const memoryUsage = this.getMemoryUsage();
    const averageRenderTime = this.getAverageMetric('render');
    const averageOperationTime = this.getAverageMetric('operation');
    return {
      fps: currentFPS,
      memory: memoryUsage,
      averageRenderTime,
      averageOperationTime,
      totalMetrics: this.metrics.length,
    };
  }
  /**
   * Ajoute un observateur pour les m\u00e9triques;
   */
  public addObserver(callback: (metrics: PerformanceMetric[]) => void): void {
    this.observers.push(callback);
  }
  /**
   * Supprime un observateur;
   */
  public removeObserver(callback: (metrics: PerformanceMetric[]) => void): void {
    const index = this.observers.indexOf(callback);
    if (index > -1) {
      this.observers.splice(index, 1);
    }
  }
  /**
   * D\u00e9marre le monitoring FPS;
   */
  private startFPSMonitoring(): void {
    const measureFPS = () => {
      if (!this.isMonitoring) return;
      const now = performance.now();
      this.frameCount++;
      if (this.lastFrameTime > 0) {
        const delta = now - this.lastFrameTime;
        const fps = 1000 / delta;
        this.fpsHistory.push(fps);
        // Garder seulement les 60 derni\u00e8res valeurs;
        if (this.fpsHistory.length > this.maxHistorySize) {
          this.fpsHistory = this.fpsHistory.slice(-this.maxHistorySize);
        }
        this.recordMetric('fps', fps, 'fps');
      }
      this.lastFrameTime = now;
      requestAnimationFrame(measureFPS);
    };
    requestAnimationFrame(measureFPS);
  }
  /**
   * D\u00e9marre le monitoring de la m\u00e9moire;
   */
  private startMemoryMonitoring(): void {
    const measureMemory = () => {
      if (!this.isMonitoring) return;
      if ('memory' in performance) {
        interface PerformanceMemory {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        }
        interface PerformanceWithMemory extends Performance {
          memory?: PerformanceMemory;
        }
        const memory = (performance as PerformanceWithMemory).memory;
        if (memory) {
          this.recordMetric('memory', memory.usedJSHeapSize, 'bytes');
        }
      }
      setTimeout(measureMemory, 1000); // Mesurer toutes les secondes;
    };
    measureMemory();
  }
  /**
   * Obtient le FPS actuel;
   */
  private getCurrentFPS(): number {
    if (this.fpsHistory.length === 0) return 0;
    const recentFPS = this.fpsHistory.slice(-10); // 10 derni\u00e8res valeurs;
    return recentFPS.reduce((sum, fps) => sum + fps, 0) / recentFPS.length;
  }
  /**
   * Obtient l'utilisation m\u00e9moire actuelle;
   */
  private getMemoryUsage(): number {
    if ('memory' in performance) {
      interface PerformanceMemory {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      }
      interface PerformanceWithMemory extends Performance {
        memory?: PerformanceMemory;
      }
      const memory = (performance as PerformanceWithMemory).memory;
      return memory?.usedJSHeapSize || 0;
    }
    return 0;
  }
  /**
   * V\u00e9rifie les seuils de performance;
   */
  private checkThresholds(metric: PerformanceMetric): void {
    let warning = false;
    let message = '';
    switch (metric.name) {
      case 'fps':
        if (metric.value < this.thresholds.fps) {
          warning = true;
          message = `\u26a0\ufe0f FPS bas: ${metric.value.toFixed(1)}fps (seuil: ${this.thresholds.fps}fps)`;
        }
        break;
      case 'memory':
        if (metric.value > this.thresholds.memory) {
          warning = true;
          message = `\u26a0\ufe0f M\u00e9moire \u00e9lev\u00e9e: ${this.formatBytes(metric.value)} (seuil: ${this.formatBytes(this.thresholds.memory)})`;
        }
        break;
      case 'render':
        if (metric.value > this.thresholds.renderTime) {
          warning = true;
          message = `\u26a0\ufe0f Temps de rendu \u00e9lev\u00e9: ${metric.value.toFixed(2)}ms (seuil: ${this.thresholds.renderTime}ms)`;
        }
        break;
      case 'operation':
        if (metric.value > this.thresholds.operationTime) {
          warning = true;
          message = `\u26a0\ufe0f Op\u00e9ration lente: ${metric.value.toFixed(2)}ms (seuil: ${this.thresholds.operationTime}ms)`;
        }
        break;
    }
    if (warning) {
      const now = Date.now();
      const lastLogTime = this.lastWarningMessages.get(metric.name) || 0;
      // Éviter les logs répétés - seulement si le throttle est passé
      if (now - lastLogTime > this.warningThrottleMs) {
        logger.warn(message);
        this.lastWarningMessages.set(metric.name, now);
      }
    }
  }
  /**
   * Notifie les observateurs
   */
  private notifyObservers(): void {
    this.observers.forEach(callback => {
      try {
        callback(this.metrics);
      } catch (error) {
        logger.error('Error in observer callback:', error);
      }
    });
  }

  /**
   * Formate les bytes en format lisible
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
  /**
   * Exporte les m\u00e9triques en JSON;
   */
  public exportMetrics(): string {
    return JSON.stringify(
      {
        metrics: this.metrics,
        stats: this.getPerformanceStats(),
        timestamp: Date.now(),
      },
      null,
      2
    );
  }
  /**
   * Importe les m\u00e9triques depuis JSON;
   */
  public importMetrics(jsonData: string): void {
    try {
      const data = JSON.parse(jsonData);
      if (data.metrics && Array.isArray(data.metrics)) {
        this.metrics = data.metrics;
        this.notifyObservers();
      }
    } catch (error) {
      logger.error('Error importing metrics:', error);
    }
  }
}

// Instance globale du moniteur de performance
export const performanceMonitor = new PerformanceMonitor();
